
import { LightningElement, api } from 'lwc';


import { isOutOfDate, handleTimeSensitiveField } from '../../../utils/basicUtils.js';
import {iconPath} from '../../../utils/iconMapper.js';

let KEY_ID = 29929;



const IS_RETURN_EMPTY_ARR = (fields) => {
    if (fields === undefined || !fields || !fields?.length) return [];
}


const HAS_ADDRESS = (rec) => {
    return rec?.fields.length && rec?.fields.some(f => f?.dtype === 'address');
}
const ADDRESS_PARTS = [
    'Street',
    'City',
    'State',
    'PostalCode',
    'Country',
];
const HANDLE_ADDRESS = (fields) => {
    if (IS_RETURN_EMPTY_ARR(fields)) return [];
    const addrFields = fields.filter(f => f?.dtype === 'address');
    if (!addrFields.length) return fields;

    // Figure out prefixes like "PhysicalAddress__"
    const prefixes = new Set(
        addrFields.map(f => {
            const token = ADDRESS_PARTS.find(part => f.fieldApiName?.includes(part));
            if (!token) return null;
            return f.fieldApiName.split(token)[0]; // keep prefix as-is
        }).filter(Boolean)
    );

    let newFields = [...fields];

    for (const prefix of prefixes) {
        // All fields that belong to this address
        const fieldsForPrefix = newFields.filter(f =>
            f.fieldApiName?.startsWith(prefix) &&
            ADDRESS_PARTS.some(part => f.fieldApiName.includes(part))
        );

        if (!fieldsForPrefix.length) continue;

        // Get index of first address-prefix occurrence
        const firstIndex = newFields.findIndex(f =>
            fieldsForPrefix.some(ff => ff.fieldApiName === f.fieldApiName)
        );

        // Get label of address fields
        const label =
            fieldsForPrefix.find(f => f?.label)?.label ??
            `${prefix.replace(/[_]+$/, '')} Address`;

        const addressEntry = {
            isaddress: true,
            dtype: 'address',
            fieldApiName: `${prefix}Address`, // synthetic
            keyId: newFields[firstIndex]?.keyId,
            label
        };

        ADDRESS_PARTS.forEach(part => {
            const match = fieldsForPrefix.find(f => f.fieldApiName.includes(part));
            addressEntry[part.toLowerCase()] = match?.displayValue ?? match?.value ?? null;
        });

        // Remove original fields for this prefix
        newFields = newFields.filter(f =>
            !fieldsForPrefix.some(ff => ff.fieldApiName === f.fieldApiName)
        );

        // Insert combined entry
        newFields.splice(firstIndex, 0, addressEntry);
    }

    return newFields;
};


const GET_ICON_BASE = (icon) => {
    if (!icon) return [null, null];
    if (typeof icon  === 'string' && icon?.split(':')?.length === 2 ) {
        const [category, value] = icon.split(':');
        return [category, value];
    }
    if (typeof icon === 'object' && icon?.icon?.split(':')?.length === 2) {
        const [category, value] = icon.icon.split(':');
        return [category, value];
    }
    return [null, null];
}
const GET_ICON_ATRRIBUTES = (icon) => {
     
    if (typeof icon !== 'object') return {sizeCl: '', iconCl: '', iconTitle: ''};

    return {
        sizeCl: icon?.size ? `slds-icon_${icon.size}` : '', 
        iconCl: icon?.iconCl ?? '',
        iconTitle: icon?.iconTitle ?? ''
    };
}
const HANDLE_ICON_FORMULAS = (fields) => {
    try {
        if (IS_RETURN_EMPTY_ARR(fields)) return [];
        const hasIconFormulas =  fields?.length && fields?.some(f => f?.iconFormula && typeof f.iconFormula === 'function');
        if (!hasIconFormulas) return fields;

        return fields.map(f => {
            if (f?.iconFormula && typeof f?.iconFormula === 'function') {
                const icon = f.iconFormula(f) ?? null;
                if (icon) {
                    const [category, value] = GET_ICON_BASE(icon);
                    const {sizeCl, iconCl, iconTitle} = GET_ICON_ATRRIBUTES(icon);
                    const iconConfig = iconPath(`${category}:${value}`, true);
                    if (iconConfig) {
                        iconConfig.svg = `slds-icon ${sizeCl ?? 'slds-icon_small'} ${iconCl ?? 'slds-current-color'}`;
                        iconConfig.iconTitle = iconTitle ?? f?.value ?? f?.displayValue ?? 'N/A';
                        return { ...f, icon: iconConfig};
                    }
                    return f;
                } 
                return f;
            }
            return f;
        });
    } catch(error) {
        console.error('Error in HANDLE_ICON_FORMULAS() of sldsFormElementStatic.js: ', error, 'error');
    }
}

const HANDLE_EXPIRED_FIELDS = (fields) => {
    try {
        if (IS_RETURN_EMPTY_ARR(fields)) return [];
        const hasTimeSensitive = fields?.some(f => f.timeSensitive);
        
        if (hasTimeSensitive) {
            //console.log('has timeSensitive fields in sldsFormElementStatic.js: ', hasTimeSensitive);
            return fields.map(f => {
                if (!f.timeSensitive || !isOutOfDate(f?.value)) {
                    return f;
                }
                const expiredField = handleTimeSensitiveField(f, true);
                
                if (expiredField) {
                    //console.log('here is expiredField in sldsFormElementStatic.js: ', JSON.stringify(expiredField));
                    return expiredField;
                }
                return f;
            });
        }
        return fields;
    } catch(error) {
        console.error('Error in HANDLE_EXPIRED_FIELDS() of sldsFormElementStatic.js: ', error, 'error');
    }
}

const HANDLE_FIELDS = (fields) => {
    try {
        if (IS_RETURN_EMPTY_ARR(fields)) return [];
        const addrfields = HANDLE_ADDRESS(fields);
        const iconFields = HANDLE_ICON_FORMULAS(addrfields);
        const expiredFields = HANDLE_EXPIRED_FIELDS(iconFields);
        return expiredFields?.map(f => ({...f, [`is${f?.dtype ?? 'text'}`]: true, keyId: f?.keyId ?? KEY_ID++}));
    } catch(error) {
        console.error('Error in HANDLE_FIELDS() of gwFormElementInlineEdit.js: ', error, 'error');
    }
}


const IS_FLAT_FIELDS_ARRAY = (rows) => {
    return Array.isArray(rows) &&
        rows.length > 0 &&
        rows.every(
            (row) =>
                row &&
                typeof row === 'object' &&
                'fieldApiName' in row &&
                !('fields' in row) // make sure it's not already normalized
        );
}

function castToType(val, typeName) {
    switch (typeName) {
        case 'number':
            return Number(val);
        case 'string':
            return String(val);
        case 'boolean':
            return Boolean(val);
        case 'object':
            // could be null, array, or Date − customize if needed
            return val; 
        default:
            return val;
    }
}


export default class gwFormElementInlineEdit extends LightningElement {
    _editingField;
    _inputChildComponent;

    _draftValues = [];
    _draftOriginalValues = [];

    _rows = [];   
    formElementCl = 'slds-form-element slds-form-element_edit slds-form-element_readonly slds-hint-parent slds-col slds-p-horizontal_x-small';

    _isEditMode = false;

    @api isSplit = false;
    @api suppressLabels = false;
    @api suppressBorder = false;

    @api
    set rows(val) {
        try {
            let rows = Array.isArray(val) ? val : [];

            KEY_ID = 29929;

            // If caller passed a flat array of fields, normalize it
            if (IS_FLAT_FIELDS_ARRAY(rows)) {
                const idField = rows.find(
                    (f) => typeof f.fieldApiName === 'string' &&
                        f.fieldApiName.toLowerCase() === 'id'
                );

                const recordId = idField?.value ?? idField?.displayValue ?? '111111111';

                rows = [
                    {
                        Id: recordId,
                        keyId: KEY_ID++,
                        fields: rows.filter(
                            (f) =>
                                !(
                                    typeof f.fieldApiName === 'string' &&
                                    f.fieldApiName.toLowerCase() === 'id'
                                )
                        )
                    }
                ];
            }

            this._rows = rows
                .map((rec) =>
                    rec
                        ? { ...rec, keyId: rec?.keyId ?? KEY_ID++, fields: HANDLE_FIELDS(rec.fields ?? []) }
                        : null
                )
                .filter(Boolean) ?? [];

        } catch (error) {
            console.error(
                'Error in set rows() of sldsFormElementStatic.js: ',
                error,
                'error'
            );
        }
    }
    get rows() {
        return this._rows;
    }
    

    // ============================== CSS Vars ============================== \\
    @api 
    set horizontal(val) {
        if (val) {
           this.formElementCl = `${this.formElementCl} slds-form-element_horizontal`;
        }
        this._horizontal = val;
    }
    get horizontal() {
        return this._horizontal;
    }
    @api 
    set labelWidth(val) {
        this._labelWidth = val;
        //console.log('here is val in set labelWidth() of gwFormElementInlineEdit.js: ', val);
        if (val) {
            const valPct = val.endsWith('%') ? val : `${val}%`;
            //console.log('here is val pct in set labelWidth() of gwFormElementInlineEdit.js: ', valPct);
            this._applyVarsDirect('--gw-form-label-width', valPct);

        }
    }
    get labelWidth() {
        return this._labelWidth;
    }
    @api 
    set valueColor(value) {
        this._valueColor = value;
        if (value) {
            this._applyVarsDirect('--gw-form-value-color', value)
        }
    } 
    get valueColor() {
        return this._valueColor;
    }
    @api 
    set border(value) {
        this._border = value;
        if (value) {
            this._applyVarsDirect('--gw-form-border', value);
        }
    } 
    get border() {
        return this._border;
    }
    @api 
    set labelColor(value) {
        this._labelColor = value;
        if (value) {
            this._applyVarsDirect('--gw-form-label-color', value)
        }
    } 
    get labelColor() {
        return this._labelColor;
    }
    @api 
    set valueFontWeight(value) {
        this._valueFontWeight = value;
        if (value) {
            this._applyVarsDirect('--gw-form-value-font-weight', value);
        }
    } 
    get valueFontWeight() {
        return this._valueFontWeight;
    }
    @api 
    set labelFontWeight(value) {
        this._labelFontWeight = value;
        if (value) {
            this._applyVarsDirect('--gw-form-label-font-weight', value);
        }
    } 
    get labelFontWeight() {
        return this._labelFontWeight;
    }
    // ====================================================================== \\

    // =========================== Public Methods =========================== \\
    @api getValues() {
        try {
            if (this._rows?.length === 1) {
                const firstRow = this._rows[0];
                const fields = firstRow.fields;
                const fieldsObj = Object.fromEntries(
                    (fields ?? []).map(f => [f.fieldApiName, f.value]) 
                );
                return fieldsObj;
            }
        } catch(error) {
            this.showToast('Error in getValues() of gwFormElementInlineEdit.js', error, 'error');
        } 
    }
    // ====================================================================== \\
    

    handleEdit(event) {
        try {
            const fieldApiName = event.currentTarget.dataset.fieldApiName;

            this._rows = this._rows.map(row => {
                return {
                    ...row,
                    fields: row.fields.map(f => {
                        const isEditing = f.fieldApiName === fieldApiName;

                        // track the editing field
                        if (isEditing) this._editingField = f;
                        
                        return { ...f, isEditing };
                    })
                };
            });

            // Focus the input element and track the input
            setTimeout(() => {
                const sldsInput = this.template.querySelector('x-slds-input');
                if (sldsInput) {
                    this._inputChildComponent = sldsInput;
                    sldsInput.focusMe();
                }
            });

        } catch(error) {
            console.error('Error in handleEdit() of gwFormElementInlineEdit.js: ', error, 'error');
        }
    }

    handleEditCancel() {
        this._rows = this._rows.map(r => ({
            ...r, fields: r.fields.map(f => {
                return f.isEditing ? {...f, isEditing: false} : f;
            })
        }))
    }

    handleEditedField() {
        try {
            const fieldApiName = this._editingField?.fieldApiName;
            const inputChildComponent = this._inputChildComponent ?? this.template.querySelector('x-slds-input');
            if (this._inputChildComponent) {
                const val = inputChildComponent.getValue(); 

                if (!this._draftOriginalValues.some(f => f.fieldApiName === fieldApiName)) {
                    // Add the field's original value to draft original values
                    //console.log('Adding this value  to original values: ', this._editingField.value);
                    // Store the original value of the field
                    this._draftOriginalValues = [...this._draftOriginalValues, {fieldApiName, value: this._editingField.value}];
                } 

                // Check if field equals current value or if equals original value
                const valType = typeof(val);
                let isEdited = castToType(this._editingField?.value, valType)  !== val;

                const origEntry = this._draftOriginalValues.find(f => f?.fieldApiName === fieldApiName);

                if (origEntry) {
                    //console.log('here is type of origEntry.value: ', typeof origEntry.value, ' here is type  of value: ', typeof val);
                }

                if (origEntry && castToType(origEntry.value, valType) === val ) isEdited = false;

                // Remove from drafts if exists there
                this._draftValues = this._draftValues.filter(f => f.fieldApiName !== fieldApiName);

                if (isEdited) {
                    // Update draft if edited
                    this._draftValues = [...this._draftValues, {fieldApiName, value: val}];
                }

                this._rows = this._rows.map(rec => {
                    return {
                        ...rec, 
                        fields: rec.fields.map(f => {
                            if (f.fieldApiName === fieldApiName) {
                                // Get the field's display value 
                                const displayValue = f.displayValueFormula ? f.displayValueFormula(val) : val;
                                // Update the field with the value sent & flag as edited
                                return {...f, isEditing: false, isEdited, value: val, displayValue};
                            }
                            // flag all field as not editing
                            return f.isEditing 
                                ? {...f, isEditing: false} 
                                : f;
                        })
                    }
                });

                const formWrapper = this.template.querySelector(`[data-form-element="${fieldApiName}"]`);
                if (formWrapper) {
                    const action = isEdited ? 'add' : 'remove';
                    //console.log('here is action ', action);
                    formWrapper.classList[action]('gw-is-edited');
                }
            }

            if (fieldApiName) {
                setTimeout(() => {
                    const button = this.template.querySelector(`[data-field-api-name="${fieldApiName}"]`)
                    if (button) {
                        button.focus();
                    }
                })
            }
            
            

        } catch(error) {
            console.error('Error in handleEditedField() of gwFormElementInlineEdit.js: ', error, 'error');
        }
    }

    handleUndo(event) {
        const fieldApiName = event.currentTarget.dataset.fieldApiName;
        if (!fieldApiName) return;

        const origDraft = this._draftOriginalValues.find(f => f.fieldApiName === fieldApiName);
            if (origDraft) {
                this._rows = this._rows.map(rec => {
                return {
                    ...rec,
                    fields: rec.fields.map(f=> {
                        if (f.fieldApiName === fieldApiName) {
                            return {
                                ...f, 
                                value: origDraft.value, 
                                displayValue: f.displayValueFormula ? f.displayValueFormula(origDraft.value) : origDraft.value,
                                isEdited: false
                            };
                        }
                        return f;
                    })

                }
            });
        }
        const container = this.template.querySelector(`[data-form-element="${fieldApiName}"]`);
        if (container) {
            container.classList.remove('gw-is-edited');
        }
    }




    // Apply Styles to the host 👉 Can pass 1.Array of Arrays   2.Array of Object   3.Strings
    _applyVarsDirect(cssVar, varStyle=null) {
        //console.log('IN _applyVarsDirect() OF gwFormElementInlineEdit.js');
        // Check that CSS var starts with '--' and has a non-null stlye 
        const isValid = (cssVarP, varStyleP) => {
            return typeof cssVarP === 'string' && 
                typeof varStyleP === 'string' &&
                cssVarP?.startsWith('--') && 
                varStyleP;
        }
 

        if (Array.isArray(cssVar)) {
            // Handle Array of vars
            cssVar.forEach(hostVar => {
                if (Array.isArray(hostVar) && isValid(hostVar[0], hostVar[1])) {
                    // Handle Array structure  (CSS var at index 0)
                    this.style.setProperty(hostVar[0], hostVar[1]);
                } else if (typeof hostVar === 'object' && isValid(hostVar.cssVar, hostVar.varStyle)) {
                    // Handle Object structure (cssVar & varStyle keys)
                    this.style.setProperty(hostVar.cssVar, hostVar.varStyle)
                }
            });
        } else if (isValid(cssVar, varStyle)) {
            // Handle string arguments
            this.style.setProperty(cssVar,  varStyle);
        }
    }

    toastObj;
    showToast(title, message, variant, mode=null) {
        const modeP = mode 
            ? mode 
                : variant === 'success'
            ? 'dismissible'
            : 'sticky';
        
        this.toastObj = { 
            variant: variant, 
            mode: modeP, 
            message: message,
            header: { 
                text: title
            }        
        };
    }
}